import { useEffect, useRef, useState } from 'preact/hooks';
import { createPortal } from 'preact/compat';
import { MiraseeCheckoutConfig, MiraseeWelcomePageConfig } from 'server/types/pmts-overrides';
import { useDocumentTitle } from 'client/utils/use-document-title';
import { useAsyncEffect } from 'client/utils/use-async-effect';
import { ProgressBar } from '@components/progress-bar';
import { SetPasswordForm } from '../checkout/set-password-form';
import { showError } from '@components/app-error';
import { rpx } from 'client/lib/rpx-client';
import { PriceRow } from 'server/types';
import * as pmtmath from 'shared/payments/math';
import * as pmtfmt from 'shared/payments/fmt';
import { VideoEmbed } from '../campaign/video-embed';
import { AddToCalendarDropdown } from '@components/add-to-calendar';
import { getBrand } from 'shared/campaign-brands';

/**
 * Optional: put this in the thank-you body HTML to force where the password
 * card goes. Without it the card goes just above the body's calendar section
 * (see findCalendarBlock), or after the body when there is none.
 */
export const SET_PASSWORD_MARKER = '{{set_password}}';
const SET_PASSWORD_SLOT_HTML = '<div data-set-password-slot></div>';

/**
 * The calendar section of a thank-you body, so the password card can go just
 * above it without the body having to say where (Lizzie, 2026-10-12): the
 * first short element that mentions the calendar, widened to the block around
 * it that holds the add-to-calendar links (Google / Outlook / Yahoo / iCal).
 * Undefined when the body has no such section.
 */
function findCalendarBlock(root: HTMLElement): HTMLElement | undefined {
  const candidates = Array.from(
    root.querySelectorAll<HTMLElement>('h1,h2,h3,h4,h5,p,strong,b,span,div'),
  );
  const heading = candidates.find((el) => {
    const text = el.textContent || '';
    return text.length <= 160 && /calendar/i.test(text);
  });
  if (!heading) {
    return;
  }
  const hasLinks = (el: HTMLElement) =>
    /\b(google|outlook|yahoo|ical|apple)\b/i.test(el.textContent || '');
  let block: HTMLElement = heading;
  while (block.parentElement && block.parentElement !== root && !hasLinks(block)) {
    block = block.parentElement;
  }
  if (!hasLinks(block)) {
    // No add-to-calendar links in the body: the nearest standalone block
    // around the heading will do.
    block = heading;
    while (
      block.parentElement &&
      block.parentElement !== root &&
      block.parentElement.children.length === 1
    ) {
      block = block.parentElement;
    }
  }
  // Widen to the whole card. The text column of a card sits beside an icon
  // in a flex row; landing the card there squeezed the column to a sliver
  // (Lizzie, 2026-10-12). Keep climbing while the parent adds no text of its
  // own, or lays its children out side by side.
  const text = (el: HTMLElement) => (el.textContent || '').replace(/\s+/g, ' ').trim();
  const sideBySide = (el: HTMLElement) => {
    const style = getComputedStyle(el);
    if (style.display.includes('grid')) {
      return true;
    }
    return style.display.includes('flex') && !style.flexDirection.startsWith('column');
  };
  while (
    block.parentElement &&
    block.parentElement !== root &&
    (text(block.parentElement) === text(block) || sideBySide(block.parentElement))
  ) {
    block = block.parentElement;
  }
  return block === root ? undefined : block;
}

type Props = {
  config: MiraseeCheckoutConfig;
  welcomeConfig: MiraseeWelcomePageConfig;
  courseTitle: string;
  courseId: string;
  priceId?: string;
  isNewUser: boolean;
  /**
   * Set for a checkout pass holder: the purchase went to an existing
   * account they are not signed in to, so no password prompt, and a note
   * that the owner has been emailed a sign-in link.
   */
  checkoutPass?: { email: string };
  isAipMember: boolean;
  upsellAccepted?: boolean;
  aipPrice?: PriceRow;
  deferredDate?: string;
  deferredUpsellDate?: string;
  deferredUpsellCourseTitle?: string;
  provision(): Promise<void>;
  onAipPurchaseComplete?: () => Promise<unknown>;
  skipProvisioning?: boolean;
  brand?: string;
};

function SmsOptinForm({
  courseId,
  priceId,
  config,
}: {
  courseId: string;
  priceId: string;
  config: NonNullable<MiraseeWelcomePageConfig['smsOptin']>;
}) {
  const [phone, setPhone] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [checking, setChecking] = useState(true);
  const [alreadyConsented, setAlreadyConsented] = useState(false);
  const [consentFirstName, setConsentFirstName] = useState<string | undefined>();
  const [declined, setDeclined] = useState(false);
  const [declining, setDeclining] = useState(false);

  useAsyncEffect(async () => {
    try {
      const result = await rpx.campaigns.checkSmsConsent({ courseId, priceId });
      if (result.alreadyConsented) {
        setAlreadyConsented(true);
        setConsentFirstName(result.firstName);
      }
    } catch {
      // Fall through to regular form
    } finally {
      setChecking(false);
    }
  }, [courseId, priceId]);

  function handleSubmit(e: Event) {
    e.preventDefault();
    if (phone.replace(/\D/g, '').length < 7) {
      return;
    }
    setShowConfirm(true);
  }

  async function handleConfirm() {
    const digits = phone.replace(/\D/g, '');
    const cleaned = phone.startsWith('+') ? `+${digits}` : digits;
    setSubmitting(true);
    try {
      await rpx.campaigns.submitSmsOptin({ phone: cleaned, courseId, priceId });
      setShowConfirm(false);
      setSubmitted(true);
    } catch (err) {
      showError(err);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDecline() {
    setDeclining(true);
    try {
      await rpx.campaigns.declineSmsForCampaign({ courseId, priceId });
      setDeclined(true);
    } catch (err) {
      showError(err);
    } finally {
      setDeclining(false);
    }
  }

  if (checking) {
    return (
      <div style="background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 24px; margin: 24px 0; text-align: center">
        <div
          class="animate-pulse"
          style="display: flex; flex-direction: column; align-items: center; gap: 10px"
        >
          <div style="height: 20px; width: 240px; background: #e2e8f0; border-radius: 6px" />
          <div style="height: 14px; width: 180px; background: #e2e8f0; border-radius: 6px" />
        </div>
      </div>
    );
  }

  if (declined) {
    return (
      <div style="background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 20px; margin: 24px 0; text-align: center">
        <p style="color: #64748b; font-size: 14px; margin: 0">
          No worries — we won't text you for this one.
        </p>
      </div>
    );
  }

  if (alreadyConsented) {
    const greeting = consentFirstName ? `${consentFirstName}, we're` : "We're";
    return (
      <div style="background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 12px; padding: 20px; margin: 24px 0; text-align: center">
        <p style="color: #15803d; font-weight: 600; font-size: 15px; margin: 0 0 6px">
          {greeting} so glad you're here!
        </p>
        <p style="color: #4b5563; font-size: 14px; margin: 0 0 8px; line-height: 1.5">
          You're already set up to receive text reminders from Mirasee Learning.
        </p>
        <button
          type="button"
          onClick={handleDecline}
          disabled={declining}
          style="background: none; border: none; color: #94a3b8; font-size: 13px; cursor: pointer; padding: 2px; text-decoration: underline"
        >
          {declining ? 'Updating...' : "I'd prefer not to receive texts for this"}
        </button>
      </div>
    );
  }

  if (submitted) {
    return (
      <div style="background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 12px; padding: 20px; margin: 24px 0; text-align: center">
        <p style="color: #15803d; font-weight: 600; font-size: 15px; margin: 0">
          You're all set! You'll receive a confirmation text shortly.
        </p>
      </div>
    );
  }

  return (
    <div style="background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 24px; margin: 24px 0">
      {config.heading && (
        <h3 style="font-size: 17px; font-weight: 700; color: #1e293b; margin: 0 0 6px; text-align: center">
          {config.heading}
        </h3>
      )}
      {config.description && (
        <p style="font-size: 14px; color: #64748b; margin: 0 0 16px; text-align: center; line-height: 1.5">
          {config.description}
        </p>
      )}
      <form onSubmit={handleSubmit} style="max-width: 400px; margin: 0 auto">
        <div style="display: flex; flex-wrap: wrap; gap: 8px">
          <input
            type="tel"
            value={phone}
            onInput={(e) => setPhone((e.target as HTMLInputElement).value)}
            placeholder="(555) 123-4567"
            style="flex: 1; min-width: 160px; padding: 10px 14px; border: 1px solid #cbd5e1; border-radius: 8px; font-size: 15px"
          />
          <button
            type="submit"
            disabled={phone.replace(/\D/g, '').length < 7}
            style={`flex: 1; min-width: 160px; padding: 10px 20px; border-radius: 8px; background: #003da6; color: #fff; font-weight: 600; font-size: 14px; border: none; cursor: pointer; opacity: ${phone.replace(/\D/g, '').length < 7 ? '0.6' : '1'}`}
          >
            {config.buttonText || 'Yes, text me reminders'}
          </button>
        </div>
      </form>
      {showConfirm && (
        <div
          role="dialog"
          aria-modal="true"
          style="position: fixed; inset: 0; z-index: 50; display: flex; align-items: center; justify-content: center"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowConfirm(false);
            }
          }}
        >
          <div style="position: fixed; inset: 0; background: rgba(0,0,0,0.4)" />
          <div style="position: relative; background: #fff; border-radius: 16px; padding: 28px; max-width: 380px; width: 90%; box-shadow: 0 20px 60px rgba(0,0,0,0.15); text-align: center">
            <p style="font-size: 15px; font-weight: 600; color: #1e293b; margin: 0 0 2px">
              Confirm SMS from Mirasee Learning
            </p>
            <p style="font-size: 14px; color: #64748b; margin: 0 0 12px">{phone}</p>
            <p style="font-size: 13px; color: #64748b; line-height: 1.5; margin: 0 0 20px">
              By confirming, you consent to receive automated text messages from Mirasee Learning at
              the number provided, including reminders about sessions, live events, and program
              updates. Message frequency varies. Msg &amp; data rates may apply. Reply HELP for help
              or STOP to cancel at any time. Consent is not a condition of purchase. View our{' '}
              <a
                href="https://mirasee.com/terms-conditions/"
                target="_blank"
                rel="noopener noreferrer"
                style="color: #003da6; text-decoration: underline"
              >
                Terms
              </a>{' '}
              and{' '}
              <a
                href="https://mirasee.com/privacy-policy/"
                target="_blank"
                rel="noopener noreferrer"
                style="color: #003da6; text-decoration: underline"
              >
                Privacy Policy
              </a>
              .
            </p>
            <div style="display: flex; flex-direction: column; align-items: center; gap: 8px">
              <button
                type="button"
                onClick={handleConfirm}
                disabled={submitting}
                style={`padding: 10px 24px; border-radius: 8px; background: #003da6; color: #fff; font-weight: 600; font-size: 14px; border: none; cursor: pointer; opacity: ${submitting ? '0.6' : '1'}`}
              >
                {submitting ? 'Sending...' : 'Yes, send me reminders'}
              </button>
              <button
                type="button"
                onClick={() => setShowConfirm(false)}
                style="background: none; border: none; color: #94a3b8; font-size: 13px; cursor: pointer; padding: 4px"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function formatDeferredDate(dateStr: string): string {
  const d = new Date(dateStr.slice(0, 10) + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

export function WelcomeShell({
  config,
  welcomeConfig,
  courseTitle,
  brand,
  children,
}: {
  config: MiraseeCheckoutConfig;
  welcomeConfig: MiraseeWelcomePageConfig;
  courseTitle: string;
  brand?: string;
  children: preact.ComponentChildren;
}) {
  useDocumentTitle([courseTitle, 'Welcome']);

  const isStoryCoach = brand === 'story-coach';
  const brandDef = getBrand(brand);
  const heroStyle = isStoryCoach
    ? 'position: relative; padding: 60px 20px 40px; text-align: center; background: #fff'
    : 'position: relative; padding: 60px 20px 340px; text-align: center; background: radial-gradient(69.5% 64.74% at 3.29% 7.39%, #0089bd 0%, #013ea6 100%), #fff';
  const headlineColor = isStoryCoach ? '#2a2e6b' : '#fff';
  const subtitleColor = isStoryCoach ? '#555' : '#80deea';
  const bodyBg = isStoryCoach
    ? "min-height: 100vh; background: #fff; font-family: 'Gentium Book Basic', serif"
    : "min-height: 100vh; background: linear-gradient(to bottom, #f0f2f5 85%, #ffffff 100%); font-family: 'Montserrat', system-ui, -apple-system, sans-serif";
  const waveFill = isStoryCoach ? '#fff' : '#f0f2f5';
  const fontUrl = isStoryCoach
    ? 'https://fonts.googleapis.com/css2?family=Gentium+Book+Basic:ital,wght@0,400;0,700;1,400&family=Bentham&display=swap'
    : 'https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800&display=swap';
  const logoUrl = config.hero.logoUrl || brandDef.defaultLogoUrl;

  return (
    <div class="mirasee-checkout" style={bodyBg}>
      <link href={fontUrl} rel="stylesheet" />
      <style
        dangerouslySetInnerHTML={{
          __html: `
        .mirasee-checkout .bg-blue-600 { background-color: #013EA6; }
        .mirasee-checkout .text-blue-600 { color: #013EA6; }
        .mirasee-checkout .text-blue-700 { color: #013EA6; }
        .welcome-body-html ul, .welcome-body-html ol { padding-left: 1.5em; margin: 0.5em 0; }
        .welcome-body-html ul { list-style-type: disc; }
        .welcome-body-html ol { list-style-type: decimal; }
        .welcome-body-html li { margin: 0.25em 0; }
        /* Direct children only: pages built as custom HTML layouts style their own
           innards, and blanket rules would re-space them. */
        .welcome-body-html > h1, .welcome-body-html > h2, .welcome-body-html > h3 {
          color: #1e293b; font-weight: 700; line-height: 1.3; margin: 1.25em 0 0.5em;
        }
        .welcome-body-html > h1 { font-size: 1.5em; }
        .welcome-body-html > h2 { font-size: 1.3em; }
        .welcome-body-html > h3 { font-size: 1.1em; }
        .welcome-body-html > p { margin: 0.75em 0; }
        .welcome-body-html > blockquote {
          margin: 0.75em 0; border-left: 3px solid #d1d5db; padding-left: 1em; color: #4b5563;
        }
        .welcome-body-html > p a, .welcome-body-html > ul a, .welcome-body-html > ol a,
        .welcome-body-html > blockquote a, .welcome-body-html > h1 a,
        .welcome-body-html > h2 a, .welcome-body-html > h3 a {
          color: #013EA6; text-decoration: underline;
        }
        .welcome-body-html > [data-align="center"] { text-align: center; }
        .welcome-body-html > [data-align="right"] { text-align: right; }
        .welcome-body-html > :first-child { margin-top: 0; }
        .welcome-body-html > :last-child { margin-bottom: 0; }
      `,
        }}
      />

      {/* Hero */}
      <div style={heroStyle}>
        {!isStoryCoach && (
          <div style="position: absolute; bottom: -1px; left: 0; width: 100%; height: max(10vw, 60px); pointer-events: none">
            <svg
              viewBox="0 0 1440 80"
              preserveAspectRatio="none"
              style="display: block; width: 100%; height: 100%"
            >
              <path fill={waveFill} d="M0,35 C480,75 960,0 1440,40 L1440,80 L0,80 Z" />
            </svg>
          </div>
        )}
        {logoUrl && (
          <div style="margin-bottom: 24px">
            <img src={logoUrl} alt="" style="max-height: 50px; display: inline-block" />
          </div>
        )}
        <h1
          style={`color: ${headlineColor}; font-size: clamp(30px, 4.5vw, 46px); font-weight: ${isStoryCoach ? '400' : '800'}; font-family: ${isStoryCoach ? "'Bentham', serif" : 'inherit'}; line-height: 1.15; margin: 0 auto; max-width: 1000px; letter-spacing: -0.01em`}
        >
          {welcomeConfig.headline}
        </h1>
        {welcomeConfig.subtitle && (
          <p
            style={`color: ${subtitleColor}; font-size: clamp(16px, 2.2vw, 22px); font-weight: 300; margin: 16px auto 0; max-width: 920px; line-height: 1.6; letter-spacing: 0.02em`}
          >
            {welcomeConfig.subtitle}
          </p>
        )}
      </div>

      {/* Main content card — matches checkout card styling */}
      <div
        style={`max-width: 700px; margin: ${isStoryCoach ? '20px' : '-280px'} auto 0; padding: 0 20px; position: relative; z-index: 1`}
      >
        <div
          style={`background: #fff; border-radius: 16px; box-shadow: 0 4px 24px rgba(0,0,0,0.08); overflow: hidden${isStoryCoach ? '; border: 1px solid #ddd2c4' : ''}`}
        >
          <div style="padding: 40px 36px; text-align: center">{children}</div>
        </div>
      </div>
    </div>
  );
}

function formatTrialPeriod(days: number): string {
  if (days % 7 === 0) {
    const weeks = days / 7;
    return `${weeks} week${weeks === 1 ? '' : 's'}`;
  }
  return `${days} day${days === 1 ? '' : 's'}`;
}

function trialEndDate(days: number): string {
  const end = new Date();
  end.setDate(end.getDate() + days);
  return end.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

function AipUpsellCard({
  price,
  onPurchaseComplete,
}: {
  price: PriceRow;
  onPurchaseComplete: () => Promise<unknown>;
}) {
  const [showConfirm, setShowConfirm] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  const [cardLast4, setCardLast4] = useState('');
  const [cardBrand, setCardBrand] = useState('');

  useEffect(() => {
    rpx.aipUpgrade
      .getAipCardInfo({ currency: price.currency })
      .then((info) => {
        if (info.hasCard) {
          setCardLast4(info.last4);
          setCardBrand(info.brand);
        }
      })
      .catch(() => {});
  }, []);

  const priceDisplay = pmtfmt.price(price);
  const interval =
    price.paymentType === 'subscription' && price.interval ? `/${price.interval}` : '';
  const freeTrialDays = pmtmath.freeTrialPeriod({ price });

  async function handleConfirm() {
    setIsProcessing(true);
    setError('');
    try {
      // The offer is always in the currency this buyer just paid in, so their
      // saved card can always be billed in it.
      const result = await rpx.aipUpgrade.purchaseAipOneClick({ currency: price.currency });
      if (result.type === 'action') {
        setError('Additional verification is required. Please contact support.');
        setIsProcessing(false);
        return;
      }
      await rpx.aipUpgrade.provisionAipUpgrade();
      await onPurchaseComplete();
    } catch (err: any) {
      setError(err?.data?.message || err?.message || 'Something went wrong. Please try again.');
      setIsProcessing(false);
    }
  }

  const benefits = [
    'Weekly live coaching calls with Danny Iny',
    'A vibrant community of fellow entrepreneurs',
    'AI-powered tools and resources',
    'On-demand replays of all past sessions',
  ];

  return (
    <>
      <div style="margin-top: 32px; border: 2px solid #013EA6; border-radius: 14px; padding: 28px 24px; background: linear-gradient(165deg, #fafbff 0%, #f0f4ff 100%); text-align: left">
        <div style="display: flex; align-items: center; gap: 10px; margin-bottom: 16px">
          <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
            <circle cx="14" cy="14" r="14" fill="#013EA6" />
            <path
              d="M9.5 14.5l3 3 6-6"
              stroke="#fff"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <span style="font-size: 18px; font-weight: 800; color: #013EA6">Join AI Playground</span>
        </div>
        <p style="font-size: 15px; line-height: 1.6; color: #4b5563; margin: 0 0 16px">
          Continue your momentum with ongoing coaching, community, and AI resources.
        </p>
        <ul style="list-style: none; padding: 0; margin: 0 0 20px">
          {benefits.map((b) => (
            <li
              key={b}
              style="display: flex; align-items: flex-start; gap: 8px; padding: 5px 0; font-size: 14px; color: #374151"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 18 18"
                fill="none"
                style="flex-shrink: 0; margin-top: 1px"
              >
                <circle cx="9" cy="9" r="9" fill="#d1fae5" />
                <path
                  d="M5.5 9.5l2 2 5-5"
                  stroke="#16a34a"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              {b}
            </li>
          ))}
        </ul>
        <button
          type="button"
          onClick={() => setShowConfirm(true)}
          style="display: block; width: 100%; padding: 14px 24px; border: none; border-radius: 8px; background: linear-gradient(135deg, #ff6b00 0%, #ffc600 100%); color: #1a1a1a; font-size: 16px; font-weight: 700; cursor: pointer; text-align: center"
        >
          {freeTrialDays
            ? `Start your free ${formatTrialPeriod(freeTrialDays)} trial`
            : 'Join AI Playground now'}
        </button>
        <p style="font-size: 13px; color: #6b7280; margin: 10px 0 0; text-align: center">
          {freeTrialDays
            ? `$0 today, then ${priceDisplay}${interval} after ${formatTrialPeriod(freeTrialDays)}`
            : `${priceDisplay}${interval}`}
          {' · Cancel anytime'}
        </p>
      </div>

      {showConfirm && (
        <div
          style="position: fixed; inset: 0; z-index: 9999; display: flex; align-items: center; justify-content: center; background: rgba(0,0,0,0.5)"
          onClick={(e) => {
            if (e.target === e.currentTarget && !isProcessing) {
              setShowConfirm(false);
            }
          }}
        >
          <div style="background: #fff; border-radius: 16px; padding: 32px; max-width: 440px; width: calc(100% - 40px); box-shadow: 0 8px 32px rgba(0,0,0,0.2); text-align: center">
            <h3 style="font-size: 20px; font-weight: 700; color: #1a1a1a; margin: 0 0 8px">
              Confirm your membership
            </h3>
            <p style="font-size: 15px; color: #4b5563; margin: 0 0 16px; line-height: 1.5">
              {freeTrialDays ? (
                <>
                  You'll start a free {formatTrialPeriod(freeTrialDays)} trial of AI Playground.
                  After {trialEndDate(freeTrialDays)}, your card will be charged{' '}
                  <strong>
                    {priceDisplay}
                    {interval}
                  </strong>
                  .
                </>
              ) : (
                <>
                  Your card will be charged{' '}
                  <strong>
                    {priceDisplay}
                    {interval}
                  </strong>{' '}
                  for AI Playground.
                </>
              )}
            </p>
            {cardLast4 && (
              <div style="display: flex; align-items: center; justify-content: center; gap: 10px; padding: 10px 16px; background: #f9fafb; border-radius: 8px; margin: 0 0 16px">
                <span style="font-size: 14px; color: #374151">
                  <span style="text-transform: capitalize; font-weight: 500">{cardBrand}</span>
                  {' ending in '}
                  <span style="font-weight: 600">{cardLast4}</span>
                </span>
              </div>
            )}
            {error && <p style="font-size: 14px; color: #dc2626; margin: 0 0 16px">{error}</p>}
            {isProcessing ? (
              <div style="padding: 20px 0; text-align: center">
                <div style="display: flex; align-items: center; justify-content: center; gap: 10px; margin-bottom: 8px">
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 22 22"
                    style="animation: spin 1s linear infinite; color: #013EA6"
                  >
                    <circle
                      cx="11"
                      cy="11"
                      r="9"
                      stroke="currentColor"
                      stroke-width="2.5"
                      fill="none"
                      opacity="0.2"
                    />
                    <path
                      d="M11 2a9 9 0 0 1 9 9"
                      stroke="currentColor"
                      stroke-width="2.5"
                      fill="none"
                      stroke-linecap="round"
                    />
                  </svg>
                  <span style="font-size: 14px; font-weight: 500; color: #374151">
                    Processing your payment...
                  </span>
                </div>
                <p style="font-size: 12px; color: #9ca3af; margin: 0">
                  Please don't close this window.
                </p>
              </div>
            ) : (
              <div style="display: flex; gap: 12px">
                <button
                  type="button"
                  onClick={() => {
                    setShowConfirm(false);
                    setError('');
                  }}
                  style="flex: 1; padding: 12px; border: 1px solid #d1d5db; border-radius: 8px; background: #fff; color: #374151; font-size: 15px; font-weight: 600; cursor: pointer"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirm}
                  style="flex: 1; padding: 12px; border: none; border-radius: 8px; background: #013EA6; color: #fff; font-size: 15px; font-weight: 600; cursor: pointer"
                >
                  Confirm
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export function MiraseeWelcomePage({
  config,
  welcomeConfig,
  courseTitle,
  courseId,
  priceId,
  isNewUser,
  checkoutPass,
  isAipMember,
  upsellAccepted,
  aipPrice,
  deferredDate,
  deferredUpsellDate,
  deferredUpsellCourseTitle,
  provision,
  onAipPurchaseComplete,
  skipProvisioning,
  brand,
}: Props) {
  const [provisioning, setProvisioning] = useState(!skipProvisioning);
  const [progress, setProgress] = useState(0);
  // A brand-new account sets its password right here on the page, then goes
  // on to the CTA link (Bhoomi, 2026-10-09: the very next thing after paying
  // should be "create your password", hard to miss, no pop-up).
  const [passwordPending, setPasswordPending] = useState(false);
  const [upsellDismissed, setUpsellDismissed] = useState(false);

  // Only a brand-new account, created in this checkout, is asked for a
  // password here. A pass holder never is: the account is someone else's
  // until the sign-in link in their inbox says otherwise.
  const askForPassword = isNewUser && !checkoutPass;
  // The block is on unless the campaign switched it off. Off means the buyer
  // is emailed a set-password link instead, so they are never stuck.
  const setPasswordBlock = welcomeConfig.setPassword;
  const setPasswordOn = setPasswordBlock?.enabled !== false;

  function startPasswordStep() {
    if (!askForPassword) {
      return;
    }
    if (setPasswordOn) {
      setPasswordPending(true);
    } else {
      rpx.auth.emailSetPasswordLink({ courseId }).catch(() => {});
    }
  }

  useAsyncEffect(async () => {
    if (skipProvisioning) {
      startPasswordStep();
      return;
    }
    try {
      const resultPromise = provision();
      for (let i = 0; i <= 100; i += 10) {
        setProgress(i);
        await new Promise((r) => setTimeout(r, 50));
      }
      await new Promise((r) => setTimeout(r, 500));
      await resultPromise;
      setProvisioning(false);
      startPasswordStep();
    } catch (err) {
      showError(err);
      setProvisioning(false);
    }
  }, []);

  const ctaUrl = welcomeConfig.ctaUrl;
  const ctaText = welcomeConfig.ctaText;
  // A pass holder is not signed in, so the button goes through sign-in and
  // on to the same place.
  const ctaHref = checkoutPass && ctaUrl ? `/login?redirect=${encodeURIComponent(ctaUrl)}` : ctaUrl;
  const ctaLabel = checkoutPass ? 'Sign in to continue' : ctaText;
  const showCta = !!ctaText && !!ctaUrl && !passwordPending;

  async function onPasswordSet() {
    setPasswordPending(false);
    if (ctaUrl) {
      window.location.href = ctaUrl;
    }
  }

  async function onPasswordSkipped() {
    setPasswordPending(false);
    // So they are not stuck behind "Forgot your password?" later.
    rpx.auth.emailSetPasswordLink({ courseId }).catch(() => {});
  }

  // Where the password card goes (the block's Position setting). 'bottom':
  // at the end of the page, after everything else, so the buyer reads the
  // rest first (Lizzie, 2026-10-12). 'afterBody': inside the campaign's own
  // body HTML via a slot (a portal), placed by the {{set_password}} marker
  // when there is one, else just above the body's calendar section; with
  // neither, right after the body.
  const passwordPosition = setPasswordBlock?.position || 'bottom';
  const bodyHtml = (
    (upsellAccepted && welcomeConfig.upsellBodyHtml
      ? welcomeConfig.upsellBodyHtml
      : welcomeConfig.bodyHtml) || ''
  )
    .split(SET_PASSWORD_MARKER)
    .join(SET_PASSWORD_SLOT_HTML);
  const bodyRef = useRef<HTMLDivElement>(null);
  const [passwordSlot, setPasswordSlot] = useState<HTMLElement | undefined>(undefined);
  useEffect(() => {
    setPasswordSlot(undefined);
    const root = bodyRef.current;
    if (!passwordPending || passwordPosition !== 'afterBody' || !root) {
      return;
    }
    const marked = root.querySelector('[data-set-password-slot]');
    if (marked instanceof HTMLElement) {
      setPasswordSlot(marked);
      return;
    }
    const block = findCalendarBlock(root);
    if (!block?.parentElement) {
      return;
    }
    const slot = document.createElement('div');
    // Should the slot ever end up beside something, take the full row.
    slot.style.cssText = 'flex: 0 0 100%; width: 100%; grid-column: 1 / -1';
    block.parentElement.insertBefore(slot, block);
    setPasswordSlot(slot);
    return () => slot.remove();
  }, [passwordPending, passwordPosition, bodyHtml]);

  const fillIn = (s: string) => s.replace(/\{\{\s*course_title\s*\}\}/g, courseTitle);
  const passwordCard = passwordPending && (
    <div style="background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 24px; margin: 24px 0; text-align: left">
      <div style="max-width: 400px; margin: 0 auto">
        <SetPasswordForm
          title={fillIn(setPasswordBlock?.heading || 'Create your password.')}
          subtitle={fillIn(
            setPasswordBlock?.description ||
              `You'll use it to login to our portal where you'll access ${courseTitle}. Set it now and you'll be taken straight to your course.`,
          )}
          onComplete={onPasswordSet}
          onSkip={onPasswordSkipped}
        />
      </div>
    </div>
  );

  if (provisioning) {
    return (
      <WelcomeShell
        config={config}
        welcomeConfig={welcomeConfig}
        courseTitle={courseTitle}
        brand={brand}
      >
        <p style="font-size: 16px; color: #4b5563; margin: 0 0 20px">
          Please wait while we finish setting things up...
        </p>
        <div style="max-width: 400px; margin: 0 auto">
          <ProgressBar showPercent={false} progress={progress} />
        </div>
      </WelcomeShell>
    );
  }

  return (
    <WelcomeShell
      config={config}
      welcomeConfig={welcomeConfig}
      courseTitle={courseTitle}
      brand={brand}
    >
      {checkoutPass && (
        <div style="background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 12px; padding: 16px 20px; margin: 0 0 24px; text-align: center">
          <p style="color: #1e40af; font-weight: 600; font-size: 15px; margin: 0 0 4px">
            You're all set! {courseTitle} has been added to the account for {checkoutPass.email}.
          </p>
          <p style="color: #3b82f6; font-size: 13px; margin: 0">
            We've emailed a sign-in link to that address.{' '}
            <a href="/login" style="color: #1e40af">
              Already know the password? Sign in.
            </a>
          </p>
        </div>
      )}

      {deferredDate && (
        <div style="background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 12px; padding: 16px 20px; margin: 0 0 24px; text-align: center">
          <p style="color: #1e40af; font-weight: 600; font-size: 15px; margin: 0 0 4px">
            Your enrollment begins {formatDeferredDate(deferredDate)}.
          </p>
          <p style="color: #3b82f6; font-size: 13px; margin: 0">
            We'll send you an email when your access is ready.
          </p>
        </div>
      )}

      {deferredUpsellDate && (
        <div style="background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 12px; padding: 16px 20px; margin: 0 0 24px; text-align: center">
          <p style="color: #1e40af; font-weight: 600; font-size: 15px; margin: 0 0 4px">
            Your {deferredUpsellCourseTitle || 'course'} enrollment begins{' '}
            {formatDeferredDate(deferredUpsellDate)}.
          </p>
          <p style="color: #3b82f6; font-size: 13px; margin: 0">
            We'll send you an email when your access is ready.
          </p>
        </div>
      )}

      {welcomeConfig.videoUrl && (
        <VideoEmbed url={welcomeConfig.videoUrl} caption={welcomeConfig.videoCaption} />
      )}

      {(() => {
        const card = passwordPosition === 'afterBody' && passwordCard;
        return (
          <>
            {bodyHtml.trim() && (
              <div
                ref={bodyRef}
                class="welcome-body-html"
                style="font-size: 16px; line-height: 1.7; color: #4b5563; margin-bottom: 28px; text-align: left"
                dangerouslySetInnerHTML={{ __html: bodyHtml }}
              />
            )}
            {card && (passwordSlot ? createPortal(card, passwordSlot) : card)}
          </>
        );
      })()}

      {welcomeConfig.calendar && welcomeConfig.calendar.startDate && (
        <AddToCalendarDropdown
          title={welcomeConfig.calendar.title}
          startDate={welcomeConfig.calendar.startDate}
          durationMinutes={welcomeConfig.calendar.durationMinutes}
          description={welcomeConfig.calendar.description}
          recurrence={welcomeConfig.calendar.recurrence}
          location={welcomeConfig.calendar.location}
        />
      )}

      {welcomeConfig.smsOptin && priceId && (
        <SmsOptinForm courseId={courseId} priceId={priceId} config={welcomeConfig.smsOptin} />
      )}

      {passwordPosition === 'bottom' && passwordCard}

      {isAipMember && !deferredUpsellDate && showCta && (
        <a
          href={ctaHref}
          style="display: inline-block; padding: 16px 48px; border-radius: 8px; background: linear-gradient(135deg, #ff6b00 0%, #ffc600 100%); color: #1a1a1a; font-size: 17px; font-weight: 700; text-decoration: none; box-shadow: 0 2px 8px rgba(255,107,0,0.3)"
        >
          {ctaLabel}
        </a>
      )}

      {!isAipMember && aipPrice && onAipPurchaseComplete && !upsellDismissed && (
        <>
          <AipUpsellCard price={aipPrice} onPurchaseComplete={onAipPurchaseComplete} />
          <button
            type="button"
            onClick={() => setUpsellDismissed(true)}
            style="display: inline-block; margin-top: 24px; color: #6b7280; font-size: 14px; text-decoration: none; background: none; border: none; cursor: pointer"
          >
            No thanks &rarr;
          </button>
        </>
      )}

      {!isAipMember && !aipPrice && showCta && (
        <a
          href={ctaHref}
          style="display: inline-block; padding: 16px 48px; border-radius: 8px; background: linear-gradient(135deg, #ff6b00 0%, #ffc600 100%); color: #1a1a1a; font-size: 17px; font-weight: 700; text-decoration: none; box-shadow: 0 2px 8px rgba(255,107,0,0.3)"
        >
          {ctaLabel}
        </a>
      )}

      {upsellDismissed && (
        <p style="margin-top: 32px; color: #6b7280; font-size: 14px">
          Questions? Reach us at{' '}
          <a href="mailto:support@mirasee.com" style="color: #4f46e5; text-decoration: none">
            support@mirasee.com
          </a>
        </p>
      )}
    </WelcomeShell>
  );
}
